import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
import Glitch from "../../components/glitch";
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h1 {...{
      "id": "tutorial-step-1---a-basic-webhook"
    }}>{`Tutorial Step 1 - A Basic Webhook`}</h1>
    <p>{`In the first step of the tutorial, we're going to create an event handler (webhook) which is compatible with Adobe I/O Events. This webhook doesn't actually do much; it just logs the body of the request. But it provides a skeleton on which you'll build in later steps.`}</p>
    <h2 {...{
      "id": "dependencies"
    }}>{`Dependencies`}</h2>
    <p>{`To write the webhook, this tutorial uses `}<a href="https://expressjs.com/" target="_new">{`Express`}</a>{`, a minimal web framework for Node.js. To automate the parsing of JSON requests, the `}<inlineCode parentName="p">{`body-parser`}</inlineCode>{` package is used. Finally, a package named `}<inlineCode parentName="p">{`dotenv`}</inlineCode>{` is used to parse and load the `}<inlineCode parentName="p">{`.env`}</inlineCode>{` file created in the `}<a parentName="p" {...{
        "href": "/experience-cloud/cloud-manager/tutorial/index.md"
      }}>{`Overview`}</a>{`. To install these three packages, run`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`npm install express body-parser dotenv
`}</code></pre>
    <h2 {...{
      "id": "writing-the-webhook-script"
    }}>{`Writing the Webhook Script`}</h2>
    <p>{`Now you're ready to write the webhook script itself. Create a new file named `}<inlineCode parentName="p">{`index.js`}</inlineCode>{` and open it in your IDE.`}</p>
    <p>{`The webhook script itself has four main parts. The first part is including the dependencies, loading the `}<inlineCode parentName="p">{`.env`}</inlineCode>{` file, and setting up the Express application:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const express = require("express");
const bodyParser = require("body-parser");

require("dotenv").config();

const app = express();

app.use(bodyParser.json());
`}</code></pre>
    <p>{`The second and third parts are the Express `}<em parentName="p">{`routes`}</em>{`. These are JavaScript functions that handle specific request patterns. An Adobe I/O webhook must handle two different types of requests.`}</p>
    <ol>
      <li parentName="ol">{`It must handle GET requests with a `}<inlineCode parentName="li">{`challenge`}</inlineCode>{` query string parameter by responding with the `}<inlineCode parentName="li">{`challenge`}</inlineCode>{` parameter.`}</li>
      <li parentName="ol">{`It must handle POST requests by responding with a 200 status code. This is how the actual events will be received.`}</li>
    </ol>
    <p>{`For the purpose of this tutorial, the path `}<inlineCode parentName="p">{`/webhook`}</inlineCode>{` is used. This can be any path, even just `}<inlineCode parentName="p">{`/`}</inlineCode>{`, but the `}<strong parentName="p">{`same`}</strong>{` path must be used for both the GET and POST routes.`}</p>
    <p>{`The challenge handler looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`app.get('/webhook', (req, res) => {
  if (req.query['challenge']) {
    res.set('Content-Type', 'text/plain')
    res.send(req.query['challenge'])
  } else {
    console.log('No challenge')
    res.status(400)
    res.end()
  }
})
`}</code></pre>
    <p>{`For the POST handler, at this point in the tutorial, it should just log the body and then write something trivial as a response.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`app.post('/webhook', (req, res) => {
  console.log(req.body)
  res.set('Content-Type', 'text/plain')
  res.send('pong')
})
`}</code></pre>
    <p>{`The last part of the script is to start listening for requests. Here, the `}<inlineCode parentName="p">{`PORT`}</inlineCode>{` variable specified in the `}<inlineCode parentName="p">{`.env`}</inlineCode>{` file is used.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const listener = app.listen(process.env.PORT, () => {
  console.log(\`Your app is listening on port \${listener.address().port}\`);
});
`}</code></pre>
    <h2 {...{
      "id": "running-the-webhook-script"
    }}>{`Running the Webhook Script`}</h2>
    <p>{`You have at least two options to run the webhook script. The first way is to simply run it locally with`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`node index.js
`}</code></pre>
    <p>{`You should see a message that it is up and running:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`Your app is listening on port 4000
`}</code></pre>
    <h3 {...{
      "id": "creating-a-tunnel-with-ngrok"
    }}>{`Creating a Tunnel with ngrok`}</h3>
    <p>{`In order to use the webhook with Adobe I/O, it must be accessible to the public internet. But, your development machine is probably not accessible. So you need to open a `}<em parentName="p">{`tunnel`}</em>{` allowing public access to the webhook. One popular tool for doing this is `}<a href="https://ngrok.com" target="_new">{`ngrok`}</a>{`. Follow the instructions on the ngrok website to install it. Once it is installed, you can open up a tunnel by running`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`ngrok http 4000
`}</code></pre>
    <InlineAlert slots="text" mdxType="InlineAlert" />
    <p>{`If you changed the `}<inlineCode parentName="p">{`PORT`}</inlineCode>{` variable in the `}<inlineCode parentName="p">{`.env`}</inlineCode>{` file to something else, adjust the command to suit.`}</p>
    <p>{`Once running ngrok will show you two `}<em parentName="p">{`forwarding`}</em>{` addresses:`}</p>
    <pre><code parentName="pre" {...{}}>{`Forwarding                    http://e639e8fd.ngrok.io -> localhost:4000
Forwarding                    https://e639e8fd.ngrok.io -> localhost:4000
`}</code></pre>
    <InlineAlert slots="text" mdxType="InlineAlert" />
    <p>{`For the purpose of this tutorial, the free ngrok service is acceptable. Just keep in mind that every time you restart ngrok you will get a new URL.`}</p>
    <h3 {...{
      "id": "running-the-webhook-script-with-glitch"
    }}>{`Running the Webhook Script with Glitch`}</h3>
    <p>{`Alternatively, you can run the webhook script using `}<a href="https://glitch.com/" target="_new">{`Glitch`}</a>{`. Glitch is an interactive web-based code editor for Node.js applications with built-in hosting. To save you the trouble of copy and pasting the files you've created already, you can just click the button below to create a new application on Glitch pre-populated with the content of the tutorial so far.`}</p>
    <Glitch projectName="adobe-cloudmanager-api-tutorial-step1" mdxType="Glitch" />
    <p>{`In the new Glitch project, you will need to populate the `}<inlineCode parentName="p">{`.env`}</inlineCode>{` file and create the `}<inlineCode parentName="p">{`.data/private.key`}</inlineCode>{` file as describe in the `}<a parentName="p" {...{
        "href": "index.md"
      }}>{`introduction`}</a>{`.`}</p>
    <InlineAlert slots="text" mdxType="InlineAlert" />
    <p>{`Feel free to create an account with Glitch if you want to continue to use it in subsequent steps of the tutorial.`}</p>
    <h2 {...{
      "id": "registering-the-webhook-with-adobe-io"
    }}>{`Registering the Webhook with Adobe I/O`}</h2>
    <p>{`Now that you have your webhook running at a publicly accessible URL, you can register it with Adobe I/O. To do this, open the `}<a href="https://console.adobe.io/projects" target="_new">{`Adobe Developer Console`}</a>{` and open the Project you created in Step 0. Click Add to Project and select Event. Select Cloud Manager and click Next.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1280px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "32.49999999999999%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/experience-cloud/cloud-manager/static/e58032fc7b17e3cda0f9f29877000a9b/5530d/event-select-event-types.webp 320w", "/experience-cloud/cloud-manager/static/e58032fc7b17e3cda0f9f29877000a9b/0c8fb/event-select-event-types.webp 640w", "/experience-cloud/cloud-manager/static/e58032fc7b17e3cda0f9f29877000a9b/94b1e/event-select-event-types.webp 1280w", "/experience-cloud/cloud-manager/static/e58032fc7b17e3cda0f9f29877000a9b/77274/event-select-event-types.webp 1842w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/experience-cloud/cloud-manager/static/e58032fc7b17e3cda0f9f29877000a9b/dd4a7/event-select-event-types.png 320w", "/experience-cloud/cloud-manager/static/e58032fc7b17e3cda0f9f29877000a9b/0f09e/event-select-event-types.png 640w", "/experience-cloud/cloud-manager/static/e58032fc7b17e3cda0f9f29877000a9b/bbbf7/event-select-event-types.png 1280w", "/experience-cloud/cloud-manager/static/e58032fc7b17e3cda0f9f29877000a9b/917f8/event-select-event-types.png 1842w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/experience-cloud/cloud-manager/static/e58032fc7b17e3cda0f9f29877000a9b/bbbf7/event-select-event-types.png",
            "alt": "Select Event Types",
            "title": "Select Event Types",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`Select the events you want to subscribe to. For the purpose of this tutorial, you will need `}<strong parentName="p">{`at least`}</strong>{` the Pipeline Execution Started event. Click the Next button.`}</p>
    <p>{`There are three options for receiving events: Journaling, Webhooks, and Runtime actions. For the purpose of this tutorial, select the Webhook option.`}</p>
    <p>{`If you are using ngrok, the Webhook URL will be the Forwarding address appended with `}<inlineCode parentName="p">{`/webhook`}</inlineCode>{`, e.g. `}<inlineCode parentName="p">{`https://e639e8fd.ngrok.io/webhook`}</inlineCode>{`. If you are using Glitch, the URL will be the Glitch application name appended with `}<inlineCode parentName="p">{`.glitch.me/webhook`}</inlineCode>{`, e.g. `}<inlineCode parentName="p">{`https://enchanted-bathroom.glitch.me/webhook`}</inlineCode></p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1120px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "67.5%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/experience-cloud/cloud-manager/static/237765ccbd9d102f61a4bace8627f183/5530d/add-webhook-to-existing-integration.webp 320w", "/experience-cloud/cloud-manager/static/237765ccbd9d102f61a4bace8627f183/0c8fb/add-webhook-to-existing-integration.webp 640w", "/experience-cloud/cloud-manager/static/237765ccbd9d102f61a4bace8627f183/6e149/add-webhook-to-existing-integration.webp 1120w"],
            "sizes": "(max-width: 1120px) 100vw, 1120px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/experience-cloud/cloud-manager/static/237765ccbd9d102f61a4bace8627f183/dd4a7/add-webhook-to-existing-integration.png 320w", "/experience-cloud/cloud-manager/static/237765ccbd9d102f61a4bace8627f183/0f09e/add-webhook-to-existing-integration.png 640w", "/experience-cloud/cloud-manager/static/237765ccbd9d102f61a4bace8627f183/1b198/add-webhook-to-existing-integration.png 1120w"],
            "sizes": "(max-width: 1120px) 100vw, 1120px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/experience-cloud/cloud-manager/static/237765ccbd9d102f61a4bace8627f183/1b198/add-webhook-to-existing-integration.png",
            "alt": "Event Registration",
            "title": "Event Registration",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <InlineAlert slots="text" variant="warning" mdxType="InlineAlert" />
    <p>{`Ensure that the Single Delivery style is selected. The tutorial code is `}<strong parentName="p">{`not`}</strong>{` intended to be used with the Batch style, although you may want to explore this on your own.`}</p>
    <h2 {...{
      "id": "next-step"
    }}>{`Next Step`}</h2>
    <p>{`With all that done, you're ready to proceed to the next step. Continue to `}<a parentName="p" {...{
        "href": "2-webhook-signature-validation.md"
      }}>{`Step 2`}</a>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      